import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { Trash2, Crown } from "lucide-react";

export default function AdminUsers() {
  const { t } = useI18n();
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  };
  useEffect(load, []);

  const onTogglePlan = async (u) => {
    const newPlan = u.plan === "pro" ? "free" : "pro";
    setBusy(u.id);
    try {
      await api.put(`/admin/users/${u.id}/plan`, null, { params: { plan: newPlan } });
      load();
    } finally { setBusy(""); }
  };

  const onDelete = async (u) => {
    if (!window.confirm(`${t("admin_users_delete_confirm")} (${u.email})?`)) return;
    setBusy(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      load();
    } finally { setBusy(""); }
  };

  return (
    <div className="surface-card overflow-hidden" data-testid="admin-users">
      <header className="px-6 py-5 border-b border-zinc-200/80 flex flex-wrap items-center gap-3">
        <h2 className="font-display font-semibold text-xl tracking-tight">{t("admin_users_title")}</h2>
        <span className="chip ms-auto">
          {users == null ? "—" : `${users.length} ${t("admin_users_count")}`}
        </span>
      </header>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-start px-6 py-3 font-medium">{t("admin_users_col_name")}</th>
              <th className="text-start px-6 py-3 font-medium">{t("admin_users_col_email")}</th>
              <th className="text-start px-6 py-3 font-medium">{t("admin_users_col_plan")}</th>
              <th className="text-start px-6 py-3 font-medium">{t("admin_users_col_apps")}</th>
              <th className="text-start px-6 py-3 font-medium">{t("admin_users_col_joined")}</th>
              <th className="text-end px-6 py-3 font-medium">{t("admin_users_col_actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {users == null ? (
              <tr><td className="px-6 py-8 text-center text-zinc-500" colSpan={6}>{t("common_loading")}</td></tr>
            ) : users.length === 0 ? (
              <tr><td className="px-6 py-8 text-center text-zinc-500" colSpan={6}>—</td></tr>
            ) : users.map((u) => (
              <tr key={u.id} className="hover:bg-zinc-50/60" data-testid={`admin-user-row-${u.id}`}>
                <td className="px-6 py-3.5 font-medium">{u.name || "—"}</td>
                <td className="px-6 py-3.5 font-mono text-xs">{u.email}</td>
                <td className="px-6 py-3.5">
                  <span className={`chip ${u.plan === "pro" ? "!bg-zinc-900 !text-white !border-zinc-900" : ""}`}>
                    {u.plan === "pro" && <Crown className="w-3 h-3" strokeWidth={2.5} />}
                    {u.plan}
                  </span>
                  {u.role === "admin" && <span className="chip ms-2">admin</span>}
                </td>
                <td className="px-6 py-3.5 font-mono text-xs">{u.apps_count}</td>
                <td className="px-6 py-3.5 text-xs text-zinc-500">{u.created_at?.split("T")[0]}</td>
                <td className="px-6 py-3.5 text-end">
                  <div className="inline-flex gap-2">
                    <button
                      onClick={() => onTogglePlan(u)}
                      disabled={busy === u.id || u.role === "admin"}
                      data-testid={`admin-user-plan-${u.id}`}
                      className="btn btn-secondary btn-sm disabled:opacity-50"
                    >
                      {u.plan === "pro" ? t("admin_users_downgrade") : t("admin_users_upgrade")}
                    </button>
                    <button
                      onClick={() => onDelete(u)}
                      disabled={busy === u.id || u.role === "admin"}
                      data-testid={`admin-user-delete-${u.id}`}
                      className="btn btn-danger btn-sm disabled:opacity-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
